import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Checkbox,
  Input,
  Button,
  message,
  Tooltip,
  type InputRef,
} from "antd";
import { useNavigate } from "react-router";
import { CheckCircleOutlined } from "@ant-design/icons";
import authService from "../../services/authService";

interface AuthCodeInputProps {
  setStep: (step: string) => void;
}

const AuthCodeInput = ({ setStep }: AuthCodeInputProps) => {
  const navigate = useNavigate();

  const [messageApi, contextHolder] = message.useMessage();
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [trustedDevice, setTrustedDevice] = useState<boolean>(false);
  const inputRefs = useRef<(InputRef | null)[]>([]);

  const showErrorMsg = (msg: React.ReactNode) => {
    messageApi.open({
      type: "error",
      content: msg,
      duration: 7,
    });
  };

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Move to next input if value is entered
    if (value !== "" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if all digits are filled
    setIsValid(newCode.every((digit) => digit !== ""));
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValid) {
      handleSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      message.error("Please paste numbers only");
      return;
    }

    const newCode = [...code];
    pastedData.split("").forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    setIsValid(newCode.every((digit) => digit !== ""));

    // Focus the next empty input or the last input
    const nextEmptyIndex = newCode.findIndex((digit) => digit === "");
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async () => {
    if (isValid) {
      const payload = {
        code: code.join(""),
        trustedDevice: trustedDevice,
      };
      try {
        const response = await authService.verifyMfaCode(payload);
        // console.log("response", response);
        if (response.status === 200) {
          //Handle successful otp verification
          navigate("/");
        } else if (response.status === 401) {
          // console.log("Invalid code", response.status);
          showErrorMsg(
            <div className="flex flex-col">
              <p className="text-[15px]">Code Not Accepted</p>
              <p>
                The code you entered is either incorrect or is from the wrong
                authenticator account.
              </p>
              <p>
                Check that you are using the latest authenticator account linked
                to your Vivo login, or contact admin support.
              </p>
            </div>,
          );
        } else if (response.status === 500) {
          // console.log("Server error", response.status);
          showErrorMsg(
            "There was a server error. Please try again later or contact the administrator for help.",
          );
        } else {
          // console.log("Error", response.status);
          showErrorMsg(
            "An unexpected error occurred. Please try again later or contact the administrator for help.",
          );
        }
      } catch (error) {
        showErrorMsg("There was an error. Please try again.");
        console.error(error);
      }
    }
  };

  const onChangeCheckBox = (e: { target: { checked: boolean } }) => {
    // console.log(`Trust device =  ${e.target.checked}`);
    setTrustedDevice(e.target.checked);
  };

  return (
    <div className="flex flex-col justify-center items-center h-screen">
      {contextHolder}
      <Card
        title="Enter Authentication Code"
        style={{ maxWidth: 400, margin: "0 auto" }}
      >
        <p className="text-[14px] text-[#556C75] mb-[30px]">
          Enter the 6-digit code from your authenticator app. Please check that
          you&apos;re using the latest account linked to your login.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
            }}
          >
            {code.map((digit, index) => (
              <Input
                key={index}
                data-testid={`otp-${index}`}
                ref={(el) => (inputRefs.current[index] = el)}
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => {
                  handleKeyDown(index, e);
                  handleKeyPress(e);
                }}
                onPaste={handlePaste}
                style={{
                  width: "48px",
                  height: "48px",
                  textAlign: "center",
                  fontSize: "20px",
                }}
              />
            ))}
          </div>
          <Tooltip title="Check this box to trust this device and avoid entering a code for 2 weeks.">
            <Checkbox className="text-[#556C75]" onChange={onChangeCheckBox}>
              Trust this device
            </Checkbox>
          </Tooltip>
          <Button
            type="primary"
            block
            onClick={handleSubmit}
            disabled={!isValid}
            icon={<CheckCircleOutlined />}
            data-testid="verify-code"
          >
            Verify Code
          </Button>

          <p className="flex flex-col items-center text-[40px] text-[#D5DADC]">
            |
          </p>
          <Button
            className="w-36 text-[#064C68] rounded-sm bg-[#D2EFF9] border-[#064C68]"
            block
            onClick={() => {
              setStep("login");
            }}
          >
            Return to Login
          </Button>
          <p className="flex flex-col items-center text-[14px] text-[#556C75] mt-[20px]">
            Having trouble with your code?{" "}
            <a href="mailto:vivo-support@omniscience.bio">
              Contact Admin Support
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AuthCodeInput;
